import { FlatList, Platform, View } from 'react-native';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { EmptyContentPlaceholder } from '@components/EmptyContentPlaceholder';
import { OrderContainer } from '@components/OrderView';
import { OrderErrorBoundary } from '@components/OrderErrorBoundary';
import { OrderSummary } from '@lib/types';
import { OrderSummaryCard } from '@components/OrderSummaryCard';
import { Typography } from '@components/Typography';
import { logger } from '@lib/logger';
import { makeStyles } from '@providers/Theme';
import { trackEvent } from '@utils/analytics';
import { updateOrderStatus } from '@lib/api';
import { useTranslation } from '@providers/I18n';
import { useIsMobile, useNavigation } from '@hooks';

type Preset = 'New' | 'Accepted' | 'History';
type OrdersListPageProps = {
  ordersList: OrderSummary[];
  loading: boolean;
  title: string;
  search?: React.ReactNode;
  noOrdersMessage: string;
  loadedAllOrdersMessage: string;
  type: Preset;
  loadMoreButton?: React.ReactNode;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    height: '100%',
    overflowX: 'auto',
    overflowY: 'auto',
    on: {
      md: {
        flexDirection: 'row',
      },
      sm: {
        flexDirection: 'column',
      },
    },
  },
  ordersList: {
    on: {
      xl: {
        flex: 1,
      },
      lg: {
        flex: 2,
      },
      md: {
        flex: 3,
        borderStyle: 'solid',
        borderRightWidth: 1,
        borderRightColor: theme.palette.grey[400],
      },
    },
  },
  title: {
    textAlign: 'center',
    marginTop: theme.spacing(4),
    on: {
      md: {
        textAlign: theme.direction === 'ltr' ? 'left' : 'right',
      },
    },
  },
  flatList: {
    ...theme.mixins.padding(2, 4),
    on: {
      md: {
        ...theme.mixins.padding(2, 4, 2, 2),
      },
    },
  },
  card: {
    marginTop: theme.spacing(2),
  },
  footer: {
    ...theme.mixins.padding(3, 0),
    fontWeight: 'bold',
  },
  desktopOrderView: {
    flex: 4,
    overflowX: 'auto',
    overflowY: 'auto',
  },
  placeholderWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  placeholderImg: {
    width: '20%',
    height: 165,
    marginBottom: theme.spacing(4),
  },
}));

const cache: any = {}; // use localStorage

export const OrdersListPage: React.FC<OrdersListPageProps> = ({
  ordersList,
  loading,
  title,
  search,
  noOrdersMessage,
  loadedAllOrdersMessage,
  type,
  loadMoreButton,
}) => {
  const navigation = useNavigation();
  const styles = useStyles();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [selectedOrder, setSelectedOrder] = useState<OrderSummary | null>(null);
  const flatListRef = useRef<FlatList>(null);

  const onOrderClick = async (order: OrderSummary) => {
    if (!order || !order?.uuid) {
      return;
    }

    if (Platform.OS === 'web') {
      window.__LAST_ACTION__ = JSON.stringify({
        type: 'order_click',
        shortCode: order.shortCode,
        state: order.state,
        time: Date.now(),
      });
      window.__LAST_ORDER__ = JSON.stringify({
        state: order.state,
        restaurantId: order.refSummary?.restaurantUUID,
        shortCode: order.shortCode,
      });
    }
    cache.clickedOrderId = order.uuid;

    if (isMobile) {
      const page =
        type === 'New'
          ? 'NewOrder'
          : type === 'Accepted'
          ? 'AcceptedOrder'
          : 'OrderHistory';

      navigation.navigate(page, {
        id: order.uuid,
      });
    } else {
      setSelectedOrder(order);
    }

    trackEvent('agent_clicked_on_order', {
      order_short_code: order.shortCode,
      order_status: order.state,
      restaurant_id: order.refSummary?.restaurantUUID,
      branch_id: order.refSummary?.branchUUID,
    });
  };

  // restore scroll on mounting
  // Inside OrdersListPage
  const initialIndex = useMemo(() => {
    if (Platform.OS !== 'web' || !cache.clickedOrderId) return undefined; // Use undefined, not null

    const index = ordersList?.findIndex((o) => o.uuid === cache.clickedOrderId);
    cache.clickedOrderId = null; 

    // CRITICAL: Only return a number if it is valid and within range
    if (index >= 0 && index < ordersList.length) {
      return index;
    }
    return undefined; 
  }, [ordersList]); // Add dependency to ensure list exists

  useEffect(() => {
    const markAsSeen = async () => {
      try {
          if (selectedOrder && ['PENDING', 'ROUTED'].includes(selectedOrder.state)) {
            // upadte order status to seen
            await updateOrderStatus({
            requestType: 'SET_ORDER_AS_SEEN',
            time: Date.now(),
            ordersUUIDs: [selectedOrder.uuid],
          });
        }
      } catch (error) {
        logger.error('Failed to update status', error);
      }
    };
    markAsSeen();
  }, [selectedOrder]);

  return (
    <View style={styles.root}>
      <View style={styles.ordersList}>
        <FlatList
          ref={flatListRef}
          contentContainerStyle={styles.flatList}
          data={ordersList}
          keyExtractor={(order) => order.uuid}
          renderItem={({ item: order }) => (
            <OrderSummaryCard
              onClick={() => onOrderClick(order)}
              style={styles.card}
              isSelected={selectedOrder?.uuid === order.uuid}
              order={order}
              key={order.uuid}
            />
          )}
          ListHeaderComponent={() => (
            <>
              <Typography size="3xl" weight="semiBold" style={styles.title}>
                {title}
              </Typography>
              {search}
            </>
          )}
          ListFooterComponent={() => {
            if (loading) {
              return (
                <Typography size="sm" weight="medium">
                  {t('global.loading', 'Loading...')}
                </Typography>
              );
            }

            if (ordersList.length === 0) {
              return (
                <Typography size="sm" weight="medium">
                  {noOrdersMessage}
                </Typography>
              );
            }

            if (loadMoreButton) {
              return <>{loadMoreButton}</>;
            }

            return (
              <Typography size="sm" weight="medium">
                {loadedAllOrdersMessage}
              </Typography>
            );
          }}
          ListFooterComponentStyle={styles.footer}
          initialScrollIndex={initialIndex}
          // ADDED THIS to prevent the crash
          onScrollToIndexFailed={(info) => {
            setTimeout(() => {
              flatListRef.current?.scrollToIndex({ index: info.index, animated: false });
            }, 500);
          }}
        />
      </View>

      {!isMobile && (
        <View style={styles.desktopOrderView}>
          {selectedOrder && (
            <OrderErrorBoundary orderUUID={selectedOrder.uuid}>
              <OrderContainer
                orderUUID={selectedOrder.uuid}
                isHistoryOrderFlag={type === 'History'}
              />
            </OrderErrorBoundary>
          )}

          {!selectedOrder && (
            <EmptyContentPlaceholder
              message={t(
                'orderView.noSelectedOrderMessage',
                'Click on any order to view its details',
              )}
            />
          )}
        </View>
      )}
    </View>
  );
};
